<template>
    <div id="internal_form" class="mx-3 py-4 px-3">
        <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 xl:px-60">
            <h3 class="text-3xl font-medium text-gray-800 sm:col-span-6">
                Shipping Address
            </h3>
            <div
                v-if="foundShippingAddress && suggestedShipping.errors"
                class="sm:col-span-6 errors mb-3"
                role="alert"
            >
                {{ suggestedShipping.errors }}
            </div>
            <div
                v-if="foundBillingAddress && suggestedBilling.errors"
                class="sm:col-span-6 errors mb-3"
                role="alert"
            >
                {{ suggestedBilling.errors }}
            </div>
            <AddressInput
                v-for="field in addressFields"
                :key="field.id"
                :field="field"
                :model="shipping"
            />
            <div class="sm:col-span-6">
                    <span class="inline-flex items-center">
                        <input
                            id="isBillingDifferent"
                            type="checkbox"
                            class="form-checkbox h-3 w-3 text-blue-600 transition duration-150 ease-in-out mr-1"
                            v-model="isBillingDifferent"
                        />
                        <label
                            for="isBillingDifferent"
                            class="block text-sm font-medium leading-5 text-gray-700"
                        >
                            Billing Address different from Shipping Address
                        </label>
                    </span>
            </div>
        </div>

        <div
            v-if="isBillingDifferent"
            class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 xl:px-60"
        >
            <h3
                class="text-3xl font-medium text-gray-800 sm:col-span-6"
            >
                Billing Address
            </h3>
            <AddressInput
                v-for="field in addressFields"
                :key="field.id"
                :field="field"
                :model="billing"
            />
        </div>
        <div class="bg-leonard-grey rounded-md grid md:grid-flow-row grid-cols-[1fr_auto_auto] gap-2 items-center my-4 p-4">
            <div></div>
            <button
                type="button"
                class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-transparent border-leonard-green text-leonard-green hover:bg-leonard-green hover:border-transparent hover:text-leonard-white focus:ring-leonard-green cursor-pointer"
                @click="cancelPayment"
            >
                Cancel
            </button>
            <button
                type="button"
                class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                :disabled="disableForms"
                @click="CheckAddress"
            >
                Continue
                <font-awesome-icon
                    v-if="disableForms"
                    icon="spinner"
                    spin
                ></font-awesome-icon>
            </button>
        </div>

        <!-- Modal Component -->
        <div
            v-if="!internalAddressVerified"
            id="internal-address-selection"
            data-backdrop="static"
            data-keyboard="false"
            class="modal fade"
            tabindex="-1"
            role="dialog"
            aria-hidden="true"
        >
            <div
                class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
                role="document"
            >
                <div class="modal-content">
                    <div class="modal-body">
                        <h3 class="text-center">Shipping Address</h3>
                        <p class="text-center">Please choose an address.</p>
                        <div
                            class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-5"
                        >
                            <div
                                data-address="provided"
                                class="sm:col-span-3"
                                @click="selectShippingAddress('provided')"
                            >
                                <div
                                    class="address-select border-dash"
                                    v-bind:class="{
                                        selected:
                                            shippingAddressSelected &&
                                            !useSuggestedShipping
                                    }"
                                >
                                    <h4>You Provided:</h4>
                                    <address>
                                        <h5>
                                            {{ shipping.firstName }}
                                            {{ shipping.lastName }}
                                        </h5>
                                        <p>{{ shipping.addressLine1 }}</p>
                                        <p v-if="shipping.addressLine2">
                                            {{ shipping.addressLine2 }}
                                        </p>
                                        <p>
                                            {{ shipping.city }},
                                            {{ shipping.state }}
                                            {{ shipping.zipCode }}
                                        </p>
                                        <p>{{ shipping.country }}</p>
                                    </address>
                                </div>
                            </div>

                            <div
                                v-if="this.shipping.country === 'United States'"
                                data-address="suggested"
                                class="sm:col-span-3"
                                @click="selectShippingAddress('suggested')"
                            >
                                <div
                                    class="address-select border-dash"
                                    v-bind:class="{
                                        selected:
                                            shippingAddressSelected &&
                                            useSuggestedShipping
                                    }"
                                >
                                    <h4>Suggested by USPS:</h4>
                                    <address>
                                        <h5>
                                            {{ suggestedShipping.firstName }}
                                            {{ suggestedShipping.lastName }}
                                        </h5>
                                        <p>
                                            {{ suggestedShipping.addressLine1 }}
                                        </p>
                                        <p
                                            v-if="
                                                suggestedShipping.addressLine2
                                            "
                                        >
                                            {{ suggestedShipping.addressLine2 }}
                                        </p>
                                        <p v-if="suggestedShipping.zipPlusFour">
                                            {{ suggestedShipping.city }},
                                            {{ suggestedShipping.state }}
                                            {{ suggestedShipping.zipCode }} -
                                            {{ suggestedShipping.zipPlusFour }}
                                        </p>
                                        <p v-else>
                                            {{ suggestedShipping.city }},
                                            {{ suggestedShipping.state }}
                                            {{ suggestedShipping.zipCode }}
                                        </p>
                                        <p>{{ suggestedShipping.country }}</p>
                                    </address>
                                </div>
                            </div>
                        </div>

                        <div v-if="isBillingDifferent">
                            <h3 class="text-center mt-5">Billing Address</h3>
                            <p class="text-center">Please choose an address.</p>
                            <div
                                class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-5"
                            >
                                <div
                                    data-address="provided"
                                    class="sm:col-span-3"
                                    @click="selectBillingAddress('provided')"
                                >
                                    <div
                                        class="address-select border-dash"
                                        v-bind:class="{
                                            selected:
                                                shippingAddressSelected &&
                                                !useSuggestedBilling
                                        }"
                                    >
                                        <h4>You Provided:</h4>
                                        <address>
                                            <address>
                                                <h5>
                                                    {{ billing.firstName }}
                                                    {{ billing.lastName }}
                                                </h5>
                                                <p>
                                                    {{ billing.addressLine1 }}
                                                </p>
                                                <p v-if="billing.addressLine2">
                                                    {{ billing.addressLine2 }}
                                                </p>
                                                <p>
                                                    {{ billing.city }},
                                                    {{ billing.state }}
                                                    {{ billing.zipCode }}
                                                </p>
                                                <p>{{ billing.country }}</p>
                                            </address>
                                        </address>
                                    </div>
                                </div>
                                <div
                                    v-if="this.billing.country === 'United States'"
                                    data-address="suggested"
                                    class="sm:col-span-3"
                                    @click="selectBillingAddress('suggested')"
                                >
                                    <div
                                        class="address-select border-dash"
                                        v-bind:class="{
                                            selected:
                                                billingAddressSelected &&
                                                useSuggestedBilling
                                        }"
                                    >
                                        <h4>Suggested by USPS:</h4>
                                        <address>
                                            <h5>
                                                {{ suggestedBilling.firstName }}
                                                {{ suggestedBilling.lastName }}
                                            </h5>
                                            <p>{{ suggestedBilling.addressLine1 }}</p>
                                            <p v-if="suggestedBilling.addressLine2">
                                                {{ suggestedBilling.addressLine2 }}
                                            </p>
                                            <p v-if="suggestedBilling.zipPlusFour">
                                                {{ suggestedBilling.city }},
                                                {{ suggestedBilling.state }}
                                                {{ suggestedBilling.zipCode }} - {{ suggestedBilling.zipPlusFour }}
                                            </p>
                                            <p v-else>
                                                {{ suggestedBilling.city }},
                                                {{ suggestedBilling.state }}
                                                {{ suggestedBilling.zipCode }}
                                            </p>
                                            <p>{{ suggestedBilling.country }}</p>
                                        </address>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer justify-center pt-0 border-t-0">
                        <button
                            type="button"
                            class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-transparent border-leonard-green text-leonard-green hover:bg-leonard-green hover:border-transparent hover:text-leonard-white focus:ring-leonard-green cursor-pointer"
                            data-dismiss="modal"
                            aria-label="Close"
                        >
                            Make Changes
                        </button>
                        <button
                            type="submit"
                            class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                            @click="checkoutInternal"
                        >
                            Place Order

                            <font-awesome-icon
                                class="ml-1"
                                v-if="submitting"
                                icon="spinner"
                                spin
                            ></font-awesome-icon>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import Cart from '../../../classes/Cart';
import AddressInput from "../../../components/AddressInput.vue";
import AddressVerificationService from "../../../classes/AddressVerificationService";

export default {
    components: {AddressInput},
    data() {
        return {
            Cart: new Cart(),

            submitting: false,
            disableForms: false,

            addressApiService: new AddressVerificationService(),
            addressFields: this.getAddressFields(),
            internalAddressVerified: false,

            foundShippingAddress: false,
            useSuggestedShipping: false,
            shippingAddressSelected: false,
            shipping: this.createAddress(),
            suggestedShipping: this.createAddress(),

            isBillingDifferent: false,
            foundBillingAddress: false,
            useSuggestedBilling: false,
            billingAddressSelected: false,
            billing: this.createAddress(),
            suggestedBilling: this.createAddress(),

            requiredFields: {
                address: [
                    "firstName",
                    "lastName",
                    "country",
                    "addressLine1",
                    "city",
                    "state",
                    "zipCode"
                ]
            }
        };
    },

    created() {
        library.add(faSpinner);
    },

    mounted() {},

    methods: {
        clearData() {
            this.shipping = this.createAddress();
            this.billing = this.createAddress();
            this.internalAddressVerified = false;
            this.submitting = false;
            this.useSuggestedShipping = false;
            this.useSuggestedBilling = false;
            this.isBillingDifferent = false;
        },

        cancelPayment() {
            this.clearData();
            this.$emit("cancelPayment");
        },

        checkoutInternal() {
            this.submitting = true;

            let self = this;
            let data = {
                user_id: this.$auth.user().details.id,
            };

            this.applySelectedAddresses();

            $("#internal-address-selection").on('hidden.bs.modal', () => {
                this.Cart.getInternal(data).then(function (response) {
                    if (!response?.success) {
                        feedback("An error occurred saving the order.", "danger");
                        return;
                    }

                    let transaction = {
                        transId: response.data.transId,
                        orderInvoiceNumber: response.data?.invoiceNumber,
                        billTo: self.isBillingDifferent ? self.billing : self.shipping,
                        shipTo: self.shipping,
                        authorization: "internal",
                    }

                    self.$emit("updateCartStatus", transaction);
                });
            });
        },

        // address methods
        createAddress() {
            return {
                firstName: null,
                lastName: null,
                addressLine1: null,
                addressLine2: null,
                country: null,
                city: null,
                state: null,
                zipCode: null,
                zipPlusFour: null,
                errors: null
            };
        },
        getAddressFields() {
            return [
                {
                    id: "firstName",
                    label: "First Name",
                    type: "text",
                    colSpan: 3
                },
                {
                    id: "lastName",
                    label: "Last Name",
                    type: "text",
                    colSpan: 3
                },
                {
                    id: "addressLine1",
                    label: "Address Line 1",
                    type: "text",
                    colSpan: 6
                },
                {
                    id: "addressLine2",
                    label: "Address Line 2",
                    type: "text",
                    colSpan: 6
                },
                { id: "city", label: "City", type: "text", colSpan: 2 },
                {
                    id: "state",
                    label: "State / Province",
                    type: "text",
                    colSpan: 2
                },
                {
                    id: "zipCode",
                    label: "Zip / Postal Code",
                    type: "text",
                    colSpan: 2,
                    maxLength: 10
                },
                {
                    id: "country",
                    label: "Country / Region",
                    type: "select",
                    options: ["United States", "Canada"],
                    colSpan: 3
                }
            ];
        },
        async verifyAddress(address, type) {
            const suggested = `suggested${type.charAt(0).toUpperCase() +
            type.slice(1)}`;
            const found = `found${type.charAt(0).toUpperCase() +
            type.slice(1)}Address`;

            this[found] = false;
            this[suggested].errors = null;

            try {
                await this.addressApiService
                    .verifyAddress(
                        address.addressLine1,
                        address.addressLine2,
                        address.city,
                        address.state,
                        address.zipCode,
                        address.country
                    )
                    .then(response => {
                        if (response.status === 200 && response.data) {
                            if (
                                response.data.dpvconfirmation === "false" &&
                                response.data.errors
                            ) {
                                this[suggested].errors = response.data.errors;
                            } else {
                                this[`${type}AddressSelected`] = true;
                                this[suggested].firstName = this[
                                    type
                                    ].firstName;
                                this[suggested].lastName = this[type].lastName;
                                this[suggested].addressLine1 = response.data.address1;
                                this[suggested].addressLine2 = response.data.address2;
                                this[suggested].city = response.data.city;
                                this[suggested].state = response.data.state;
                                this[suggested].zipCode = response.data.zip5;
                                this[suggested].zipPlusFour = response.data.zip4;
                                this[suggested].country = this[type].country;
                            }

                            this[found] = true;
                        }
                    });
            } catch (error) {
                console.error(
                    `An error occurred while verifying the address. ERR:`,
                    error
                );
            }
        },
        selectShippingAddress(selected) {
            this.useSuggestedShipping = selected !== "provided";
        },
        selectBillingAddress(selected) {
            this.useSuggestedBilling = selected !== "provided";
        },
        async CheckAddress() {
            this.disableForms = true;

            const fieldNames = {
                firstName: "First name",
                lastName: "Last name",
                addressLine1: "Address Line 1",
                city: "City",
                state: "State / Province",
                country: "Country / Region",
                zipCode: "Zip / Postal Code"
            };

            let errors = false;

            const setErrors = (model, fields) => {
                this.$set(model, "errors", {});
                fields.forEach(f => {
                    if (!model[f]) {
                        this.$set(
                            model.errors,
                            f,
                            `${fieldNames[f]} must be provided.`
                        );
                        errors = true;
                    }
                });
            };

            setErrors(this.shipping, this.requiredFields.address);
            if (this.isBillingDifferent) {
                setErrors(this.billing, this.requiredFields.address);
            }

            if (!errors && !this.validateZip(this.shipping.zipCode)) {
                this.$set(this.shipping.errors, "zipCode", "Invalid zip code.");
                errors = true;
            }

            if (
                !errors &&
                this.isBillingDifferent &&
                !this.validateZip(this.billing.zipCode)
            ) {
                this.$set(this.billing.errors, "zipCode", "Invalid zip code.");
                errors = true;
            }

            if (!errors) {
                if (this.shipping.country === "United States") {
                    await this.verifyAddress(this.shipping, "shipping");
                }

                if (
                    this.billing.country === "United States" &&
                    this.isBillingDifferent
                ) {
                    await this.verifyAddress(this.billing, "billing");
                }

                if (
                    this.suggestedShipping.errors ||
                    (this.isBillingDifferent && this.suggestedBilling.errors)
                ) {
                    console.log(this.suggestedShipping.errors);

                    if (this.isBillingDifferent) {
                        console.log(this.suggestedBilling.errors);
                    }
                } else {
                    $("#internal-address-selection").modal("show");
                }
            }

            this.disableForms = false;
        },
        applySelectedAddresses() {
            if (this.useSuggestedShipping) {
                this.shipping = { ...this.suggestedShipping };
            }

            if (this.isBillingDifferent && this.useSuggestedBilling) {
                this.billing = { ...this.suggestedBilling };
            } else if (!this.isBillingDifferent) {
                this.billing = { ...this.shipping };
            }

            this.internalAddressVerified = true;

            $("#internal-address-selection").modal("hide");
        },

        // error handling
        resetErrors(model) {
            model.errors = {};
        },
        removeErrors(a, field){
            a.errors && a.errors[field] ? a.errors[field] = null : null
        },
        validateZip(s) {
            return /^\d{5}(-\d{4})?$/.test(s);
        },
    },
};
</script>
<style lang="scss" scoped>
.errors {
    font-size: 0.75em;
    color: #e64847;
}

.not-valid {
    border-color: #e64847;
}

.form-input-wrapper:focus-within {
    outline: none;
    box-shadow: 0 0 0 3px rgba(164, 202, 254, 0.45);
    border-color: #a4cafe;
}

.form-input-wrapper input:focus {
    outline: none;
}

.address-select {
    background-color: #eff7f7;
    border-width: 2px;
    border-style: solid;
    border-color: transparent;
    border-radius: 0.375rem;
    padding: 1rem;
    cursor: pointer;

    h4 {
        color: #057a55;
        margin-bottom: 0.5rem;
    }

    address {
        font-style: normal;

        p {
            font-size: 0.875rem;
        }
    }

    &.selected {
        border-color: #057a55;
    }
}
</style>
